import store from '../store.js'
import api from '../api.js'
import { busy, notBusy } from '../busy/actions.js'
import { showPeopleDialog } from '../dialog/actions.js'
import { handleGetErrors, handleUpdateErrors } from '../error/actions.js'
import { setSelectedItem } from '../selected/actions.js'
import redux from '../index.js'
import { estimateTotalWithLookAhead, getPrefetchPagesAhead } from '../pagination/shared.js'

export const CONTACT_GET_OK_ACTION = 'CONTACT_GET_OK_ACTION'
export const CONTACT_UPDATE_ACTION = 'CONTACT_UPDATE_ACTION'
export const CONTACT_DELETE_ACTION = 'CONTACT_DELETE_ACTION'
export const CONTACT_EXPAND_NODE_ACTION = 'CONTACT_EXPAND_NODE_ACTION'

export const CONTACT_GET_PAGE_ITEMS_ACTION = 'CONTACT_GET_PAGE_ITEMS_ACTION'
export const CONTACT_GET_PAGE_ITEM_ACTION = 'CONTACT_GET_PAGE_ITEM_ACTION'
export const CONTACT_GET_PROJECTS_ACTION = 'CONTACT_GET_PROJECTS_ACTION'
export const CONTACT_SET_ORDER_ACTION = 'CONTACT_SET_ORDER_ACTION'
export const CONTACT_SET_LIMIT_ACTION = 'CONTACT_SET_LIMIT_ACTION'
export const CONTACT_SET_OFFSET_ACTION = 'CONTACT_SET_OFFSET_ACTION'
export const CONTACT_SET_SEARCH_ACTION = 'CONTACT_SET_SEARCH_ACTION'
export const CONTACT_SET_TOTAL_COUNT_ACTION = 'CONTACT_SET_TOTAL_COUNT_ACTION'
export const CONTACT_TOGGLE_SHOW_FAVORITES = 'CONTACT_TOGGLE_SHOW_FAVORITES'
export const CONTACT_TOGGLE_SHOW_ACTIVE = 'CONTACT_TOGGLE_SHOW_ACTIVE'
export const CONTACT_TOGGLE_SHOW_RECENT = 'CONTACT_TOGGLE_SHOW_RECENT'

const MAX_PREFETCH_ITEMS = 200
const contactPageCache = new Map()
const contactPageRequestsInFlight = new Map()
const combinedSearchCache = new Map()
const combinedSearchInFlight = new Map()

function invalidateContactCaches () {
  contactPageCache.clear()
  contactPageRequestsInFlight.clear()
  combinedSearchCache.clear()
  combinedSearchInFlight.clear()
}

let latestContactPageItemsRequest = 0

export function contactUpdateAction (contact) {
  return {
    type: CONTACT_UPDATE_ACTION,
    contact
  }
}

export function contactDeleteAction (contact) {
  return {
    type: CONTACT_DELETE_ACTION,
    contact
  }
}

export function getContacts (name) {
  const params = {
    query: [],
    sort: 'name.asc'
  }
  if (typeof name === 'string' && name.length > 0) {
    params.query.push({ key: 'name', value: name, op: '~' })
  } else if (typeof name === 'undefined') {
    params.limit = 25
  }

  return function (dispatch) {
    dispatch(busy())
    api.getContacts(params).then(result => {
      dispatch(notBusy())
      dispatch({ type: CONTACT_GET_OK_ACTION, items: result.data.value })
    }).catch(handleGetErrors('contact', dispatch))
  }
}

export function getContact (id) {
  const params = {
    query: [{ key: 'id', value: id, op: '=' }]
  }
  return function (dispatch) {
    dispatch(busy())
    api.getContacts(params).then(result => {
      dispatch(notBusy())
      dispatch({ type: CONTACT_GET_OK_ACTION, items: result.data.value })
    }).catch(handleGetErrors('contact', dispatch))
  }
}

export function getPageItems (id) {
  const state = store.getState()
  const {
    search,
    limit,
    offset,
    orderBy,
    order,
    showFavorites,
    showActive,
    showRecent
  } = state.contact

  const sort = orderBy ? `${orderBy}.${order}` : null
  const params = {
    query: [],
    limit,
    offset,
    sort,
    recent: showRecent
  }

  if (typeof search === 'string' && search.length > 0) {
    params.query.push({ key: 'name', value: search, op: '~' })
  }

  const hasSearch = typeof search === 'string' && search.trim().length > 0

  if (showFavorites && !hasSearch) {
    params.query.push({ key: 'is_favorite', value: showFavorites, op: '=' })
  }
  applyActiveFilter(params.query, showActive)

  return function (dispatch) {
    const requestId = ++latestContactPageItemsRequest
    dispatch(busy())

    let loadPromise
    if (hasSearch) {
      const limitNumber = Math.max(1, Number(limit) || 1)
      const offsetNumber = Math.max(0, Number(offset) || 0)
      const searchTerm = String(search || '').trim()

      loadPromise = getCombinedSearchContactsPaged({
        search: searchTerm,
        showActive,
        showRecent,
        sort,
        offset: offsetNumber,
        limit: limitNumber
      }).then((result) => {
        setTimeout(() => {
          prefetchCombinedSearchPagesAhead({
            search: searchTerm,
            showActive,
            showRecent,
            sort,
            offset: offsetNumber,
            limit: limitNumber
          })
        }, 0)
        return result
      })
    } else {
      const limitNumber = Math.max(1, Number(limit) || 1)
      const offsetNumber = Math.max(0, Number(offset) || 0)
      const cacheKey = getContactCacheKey({ query: params.query, recent: params.recent, sort: params.sort, limit: limitNumber })
      const cachedPage = getContactCachedPage(cacheKey, offsetNumber)
      const cachedTotal = getContactCachedTotal(cacheKey)

      if (cachedPage) {
        dispatch(notBusy())
        dispatch({
          type: CONTACT_GET_PAGE_ITEMS_ACTION,
          pageItems: cachedPage,
          totalCount: Math.max(Number(cachedTotal) || 0, offsetNumber + cachedPage.length)
        })
        if (id) {
          getPageItem(id)(dispatch)
        }
        setTimeout(() => {
          prefetchContactPagesAhead(cacheKey, {
            query: params.query,
            recent: params.recent,
            sort: params.sort,
            limit: limitNumber
          }, offsetNumber)
        }, 0)

        loadPromise = Promise.resolve({ pageItems: cachedPage, totalCount: Math.max(Number(cachedTotal) || 0, offsetNumber + cachedPage.length), alreadyDispatched: true })
      } else {
        loadPromise = fetchContactsPageWithCache({
          query: params.query,
          recent: params.recent,
          sort: params.sort,
          limit: limitNumber,
          offset: offsetNumber
        }, cacheKey).then(({ pageItems, totalCount }) => {
          const initialTotal = Math.max(Number(totalCount) || 0, offsetNumber + pageItems.length)

          dispatch(notBusy())
          if (requestId !== latestContactPageItemsRequest) {
            return { pageItems: [], totalCount: 0, alreadyDispatched: true }
          }
          dispatch({
            type: CONTACT_GET_PAGE_ITEMS_ACTION,
            pageItems,
            totalCount: initialTotal
          })
          if (id) {
            getPageItem(id)(dispatch)
          }

          // Keep UI responsive by rendering current page immediately, then estimate a bounded total in background.
          setTimeout(() => {
            prefetchContactPagesAhead(cacheKey, {
              query: params.query,
              recent: params.recent,
              sort: params.sort,
              limit: limitNumber
            }, offsetNumber)

            estimateTotalWithLookAhead(
              (nextParams) => fetchContactsPageWithCache(nextParams, cacheKey),
              { query: params.query, recent: params.recent },
              {
                batchSize: limitNumber,
                firstPageItems: pageItems,
                currentOffset: offsetNumber,
                maxPrefetchItems: MAX_PREFETCH_ITEMS,
                maxPagesAhead: 3
              }
            ).then((count) => {
              if (requestId !== latestContactPageItemsRequest) {
                return
              }
              const safeCount = Math.max(Number(count) || 0, pageItems.length)
              const nextTotal = Math.max(getContactCachedTotal(cacheKey), safeCount)
              setContactCachedTotal(cacheKey, nextTotal)
              dispatch({ type: CONTACT_SET_TOTAL_COUNT_ACTION, totalCount: nextTotal })
            }).catch(() => {})
          }, 0)

          return {
            pageItems,
            totalCount: initialTotal,
            alreadyDispatched: true
          }
        })
      }
    }

    loadPromise.then((result) => {
      if (!result?.alreadyDispatched) {
        dispatch(notBusy())
      }
      if (requestId !== latestContactPageItemsRequest) {
        return
      }
      if (!result?.alreadyDispatched) {
        dispatch({
          type: CONTACT_GET_PAGE_ITEMS_ACTION,
          pageItems: result.pageItems,
          totalCount: result.totalCount
        })
        if (id) {
          getPageItem(id)(dispatch)
        }
      }
    }).catch((error) => {
      dispatch(notBusy())
      if (requestId !== latestContactPageItemsRequest) {
        return
      }
      handleGetErrors('contact', dispatch)(error)
    })
  }
}

function getContactCacheKey ({ query, recent, sort, limit }) {
  return JSON.stringify({
    query: Array.isArray(query) ? query : [],
    recent: !!recent,
    sort: sort || '',
    limit: Math.max(1, Number(limit) || 1)
  })
}

function getContactCacheBucket (cacheKey) {
  if (!contactPageCache.has(cacheKey)) {
    contactPageCache.set(cacheKey, { pages: new Map(), totalCount: 0 })
  }
  return contactPageCache.get(cacheKey)
}

function getContactCachedPage (cacheKey, offset) {
  const bucket = contactPageCache.get(cacheKey)
  return bucket?.pages?.get(offset) || null
}

function setContactCachedPage (cacheKey, offset, pageItems) {
  const bucket = getContactCacheBucket(cacheKey)
  bucket.pages.set(offset, Array.isArray(pageItems) ? pageItems : [])
}

function getContactCachedTotal (cacheKey) {
  const bucket = contactPageCache.get(cacheKey)
  return Number(bucket?.totalCount) || 0
}

function setContactCachedTotal (cacheKey, totalCount) {
  const bucket = getContactCacheBucket(cacheKey)
  bucket.totalCount = Math.max(Number(totalCount) || 0, 0)
}

function fetchContactsPageWithCache (params, cacheKey) {
  const limit = Math.max(1, Number(params?.limit) || 1)
  const offset = Math.max(0, Number(params?.offset) || 0)
  const requestKey = `${cacheKey}|${offset}|${limit}`
  const existing = contactPageRequestsInFlight.get(requestKey)
  if (existing) {
    return existing
  }

  const request = api.getContacts({ ...params, limit, offset }).then((result) => {
    const pageItems = Array.isArray(result?.data?.value) ? result.data.value : []
    const totalCount = Math.max(Number(result?.data?.total_count) || 0, offset + pageItems.length)
    setContactCachedPage(cacheKey, offset, pageItems)
    setContactCachedTotal(cacheKey, totalCount)
    return { pageItems, totalCount }
  }).finally(() => {
    contactPageRequestsInFlight.delete(requestKey)
  })

  contactPageRequestsInFlight.set(requestKey, request)
  return request
}

function prefetchContactPagesAhead (cacheKey, baseParams, currentOffset) {
  const limit = Math.max(1, Number(baseParams?.limit) || 1)
  const pagesAhead = getPrefetchPagesAhead(limit, MAX_PREFETCH_ITEMS, 2)

  if (pagesAhead <= 0) {
    return
  }

  for (let i = 1; i <= pagesAhead; i++) {
    const nextOffset = currentOffset + (i * limit)
    if (getContactCachedPage(cacheKey, nextOffset)) {
      continue
    }

    fetchContactsPageWithCache({ ...baseParams, offset: nextOffset }, cacheKey).catch(() => {})
  }
}

async function getCombinedSearchContactsPaged ({ search, showActive, showRecent, sort, offset, limit }) {
  const safeLimit = Math.max(1, Number(limit) || 1)
  const safeOffset = Math.max(0, Number(offset) || 0)
  const cacheKey = getCombinedSearchCacheKey({ search, showActive, showRecent, sort, limit: safeLimit })
  const requiredCount = safeOffset + safeLimit

  await ensureCombinedSearchCoverage(cacheKey, {
    search,
    showActive,
    showRecent,
    sort,
    limit: safeLimit,
    requiredCount,
    maxItemsToLoad: MAX_PREFETCH_ITEMS
  })

  const sorted = getCombinedSearchSorted(cacheKey, sort)
  const pageItems = sorted.slice(safeOffset, safeOffset + safeLimit)
  const complete = isCombinedSearchComplete(cacheKey)
  const totalCount = complete
    ? sorted.length
    : Math.max(sorted.length, safeOffset + pageItems.length + 1)

  return {
    pageItems,
    totalCount
  }
}

function prefetchCombinedSearchPagesAhead ({ search, showActive, showRecent, sort, offset, limit }) {
  const safeLimit = Math.max(1, Number(limit) || 1)
  const safeOffset = Math.max(0, Number(offset) || 0)
  const pagesAhead = getPrefetchPagesAhead(safeLimit, MAX_PREFETCH_ITEMS, 2)
  if (pagesAhead <= 0) {
    return
  }

  const cacheKey = getCombinedSearchCacheKey({ search, showActive, showRecent, sort, limit: safeLimit })
  const requiredCount = safeOffset + (safeLimit * (1 + pagesAhead))
  ensureCombinedSearchCoverage(cacheKey, {
    search,
    showActive,
    showRecent,
    sort,
    limit: safeLimit,
    requiredCount,
    maxItemsToLoad: MAX_PREFETCH_ITEMS
  }).catch(() => {})
}

function getCombinedSearchCacheKey ({ search, showActive, showRecent, sort, limit }) {
  return JSON.stringify({
    search: String(search || '').toLowerCase(),
    active: !!showActive,
    recent: !!showRecent,
    sort: sort || '',
    limit: Math.max(1, Number(limit) || 1)
  })
}

function getCombinedSearchBucket (cacheKey) {
  if (!combinedSearchCache.has(cacheKey)) {
    combinedSearchCache.set(cacheKey, {
      contactsById: new Map(),
      projectMatchesByContactId: new Map(),
      fetchedMissingContactIds: new Set(),
      nextContactsOffset: 0,
      nextProjectsOffset: 0,
      contactsDone: false,
      projectsDone: false,
      sorted: [],
      sortedSort: '',
      dirty: true
    })
  }
  return combinedSearchCache.get(cacheKey)
}

function getCombinedSearchSorted (cacheKey, sort) {
  const bucket = getCombinedSearchBucket(cacheKey)
  if (!bucket.dirty && bucket.sortedSort === String(sort || '')) {
    return bucket.sorted
  }

  const sorted = sortByApiSort(Array.from(bucket.contactsById.values()), sort).map((contact) => {
    const matchedProjects = bucket.projectMatchesByContactId.get(contact?.id) || []
    if (matchedProjects.length === 0) {
      return contact
    }

    return {
      ...contact,
      children: matchedProjects,
      __autoExpand: true
    }
  })

  bucket.sorted = sorted
  bucket.sortedSort = String(sort || '')
  bucket.dirty = false
  return sorted
}

function isCombinedSearchComplete (cacheKey) {
  const bucket = combinedSearchCache.get(cacheKey)
  return !!(bucket && bucket.contactsDone && bucket.projectsDone)
}

function addProjectsToBucket (bucket, projects) {
  projects.forEach((project) => {
    const contactId = project?.collection_id
    if (contactId == null) {
      return
    }
    const existing = bucket.projectMatchesByContactId.get(contactId) || []
    existing.push(project)
    bucket.projectMatchesByContactId.set(contactId, existing)
  })
}

async function ensureCombinedSearchCoverage (cacheKey, {
  search,
  showActive,
  showRecent,
  sort,
  limit,
  requiredCount,
  maxItemsToLoad
}) {
  const inflightKey = `${cacheKey}|${requiredCount}`
  const existing = combinedSearchInFlight.get(inflightKey)
  if (existing) {
    return existing
  }

  const request = (async () => {
    const bucket = getCombinedSearchBucket(cacheKey)
    const safeLimit = Math.max(1, Number(limit) || 1)
    const targetCount = Math.max(0, Number(requiredCount) || 0)
    let remainingBudget = Math.max(0, Number(maxItemsToLoad) || 0)

    while (getCombinedSearchSorted(cacheKey, sort).length < targetCount && remainingBudget > 0 && (!bucket.contactsDone || !bucket.projectsDone)) {
      const requestSize = Math.max(1, Math.min(50, safeLimit, remainingBudget))
      const requests = []

      if (!bucket.contactsDone) {
        const contactsQuery = [{ key: 'name', value: search, op: '~' }]
        applyActiveFilter(contactsQuery, showActive)
        requests.push(api.getContacts({
          query: contactsQuery,
          recent: showRecent,
          sort,
          limit: requestSize,
          offset: bucket.nextContactsOffset
        }).then((result) => ({ type: 'contacts', result, requestSize })))
      }

      if (!bucket.projectsDone) {
        const projectsQuery = [{ key: 'name', value: search, op: '~' }]
        applyActiveFilter(projectsQuery, showActive)
        requests.push(api.getProjects({
          query: projectsQuery,
          recent: showRecent,
          limit: requestSize,
          offset: bucket.nextProjectsOffset
        }).then((result) => ({ type: 'projects', result, requestSize })))
      }

      if (requests.length === 0) {
        break
      }

      const responses = await Promise.all(requests)
      for (const response of responses) {
        const items = Array.isArray(response?.result?.data?.value) ? response.result.data.value : []
        const loaded = items.length
        remainingBudget = Math.max(0, remainingBudget - loaded)

        if (response.type === 'contacts') {
          bucket.nextContactsOffset += response.requestSize
          if (loaded < response.requestSize) {
            bucket.contactsDone = true
          }
          items.forEach((contact) => {
            if (contact?.id != null) {
              bucket.contactsById.set(contact.id, contact)
            }
          })
        } else {
          bucket.nextProjectsOffset += response.requestSize
          if (loaded < response.requestSize) {
            bucket.projectsDone = true
          }
          addProjectsToBucket(bucket, items)
        }
      }

      const missingIds = []
      bucket.projectMatchesByContactId.forEach((_projects, contactId) => {
        if (!bucket.contactsById.has(contactId) && !bucket.fetchedMissingContactIds.has(contactId)) {
          missingIds.push(contactId)
        }
      })

      if (missingIds.length > 0 && remainingBudget > 0) {
        const toFetch = missingIds.slice(0, Math.min(missingIds.length, remainingBudget, 20))
        const missingResponses = await Promise.all(toFetch.map((contactId) => {
          bucket.fetchedMissingContactIds.add(contactId)
          return api.getContacts({ query: [{ key: 'id', value: contactId, op: '=' }], limit: 1 })
        }))

        missingResponses.forEach((result) => {
          const items = Array.isArray(result?.data?.value) ? result.data.value : []
          items.forEach((contact) => {
            if (contact?.id != null) {
              bucket.contactsById.set(contact.id, contact)
            }
          })
          remainingBudget = Math.max(0, remainingBudget - items.length)
        })
      }

      bucket.dirty = true
    }
  })().finally(() => {
    combinedSearchInFlight.delete(inflightKey)
  })

  combinedSearchInFlight.set(inflightKey, request)
  return request
}

function sortByApiSort (items, sort) {
  const [key = 'name', direction = 'asc'] = String(sort || 'name.asc').split('.')
  const sign = direction === 'desc' ? -1 : 1

  return items.slice().sort((a, b) => {
    const av = normalizeSortValue(a?.[key])
    const bv = normalizeSortValue(b?.[key])
    if (av < bv) return -1 * sign
    if (av > bv) return 1 * sign
    return 0
  })
}

function normalizeSortValue (value) {
  if (typeof value === 'number') return value
  return String(value || '').toLowerCase()
}

export function getProjects (contactId) {
  const state = store.getState()
  const { showFavorites, showActive } = state.contact

  const params = {
    query: [{ key: 'collection_id', value: contactId, op: '=' }],
    sort: 'updated_at.desc'
  }
  if (showFavorites) {
    params.query.push({ key: 'is_favorite', value: showFavorites, op: '=' })
  }
  applyActiveFilter(params.query, showActive)

  return function (dispatch) {
    dispatch(busy())
    api.getProjects(params).then(result => {
      dispatch(notBusy())
      dispatch({
        type: CONTACT_GET_PROJECTS_ACTION,
        contactId,
        projects: result.data.value
      })
    }).catch(handleGetErrors('project', dispatch))
  }
}

function getPageItem (id) {
  const state = store.getState()
  const { sharePointSaveEnabled } = state.login.userData

  const params = {
    query: [{ key: 'id', value: id, op: '=' }],
    limit: 1
  }

  return function (dispatch) {
    dispatch(busy())
    api.getContacts(params).then(result => {
      dispatch(notBusy())
      if (Array.isArray(result.data.value) && result.data.value.length === 1) {
        dispatch({
          type: CONTACT_GET_PAGE_ITEM_ACTION,
          pageItem: result.data.value[0]
        })
        dispatch(setSelectedItem(result.data.value[0]))
        if (sharePointSaveEnabled) {
          dispatch(redux.actions.files.getRootFolder(result.data.value[0]))
        }
      }
    }).catch(handleGetErrors('contact', dispatch))
  }
}

export function showContactPeopleDialog (contactId, title) {
  return function (dispatch) {
    const query = [{ key: 'collection_id', value: contactId, op: '=' }]
    dispatch(busy())
    api.getContactPersons({ query }).then(result => {
      dispatch(notBusy())
      const people = result.data.value.map(person => {
        return {
          person_name: person.name,
          person_collection_name: person.collection_name,
          person_address: person.address?.address,
          person_city: person.address?.city,
          person_email: person.address?.email,
          person_homepage: person.address?.homepage,
          person_phone: person.address?.phone,
          person_postal_code: person.address?.postal_code,
          role_name: ''
        }
      })
      dispatch(showPeopleDialog(contactId, people, title))
    }).catch(handleGetErrors('project', dispatch))
  }
}

export function toggleExpandNode (contact) {
  return {
    type: CONTACT_EXPAND_NODE_ACTION,
    contact
  }
}

export function toggleFavorite (contact) {
  return function (dispatch) {
    const favorite = !contact.is_favorite
    const current = store.getState().contact.pageItems.find((item) => item.id === contact.id) || contact
    const updated = { ...current, is_favorite: favorite }
    dispatch(busy())
    api.setContactFavorite(contact.id, favorite).then(() => {
      invalidateContactCaches()
      dispatch(contactUpdateAction(updated))
      dispatch(notBusy())
    }).catch(handleUpdateErrors('contact', dispatch))
  }
}

/**
 * Sort order
 */
export function setOrder (orderBy, order) {
  return {
    type: CONTACT_SET_ORDER_ACTION,
    orderBy,
    order
  }
}

/**
 * Set limit for pagination
 */
export function setLimit (limit) {
  return {
    type: CONTACT_SET_LIMIT_ACTION,
    limit
  }
}

/**
 * Set offset for pagination
 */
export function setOffset (offset) {
  return {
    type: CONTACT_SET_OFFSET_ACTION,
    offset
  }
}

/**
 * Set search
 */
export function setSearch (search) {
  return {
    type: CONTACT_SET_SEARCH_ACTION,
    search
  }
}

/**
 * Toggle show favorites
 */
export function toggleShowFavorites () {
  return {
    type: CONTACT_TOGGLE_SHOW_FAVORITES
  }
}

/**
 * Toggle show active only
 */
export function toggleShowActive () {
  return {
    type: CONTACT_TOGGLE_SHOW_ACTIVE
  }
}

/**
 * Toggle show recent
 */
export function toggleShowRecent () {
  return {
    type: CONTACT_TOGGLE_SHOW_RECENT
  }
}

function applyActiveFilter (query, showActive) {
  if (!showActive) {
    return
  }

  query.push({ key: 'ended_at', value: null, op: '=' })
}
